import {
  type Certificate,
  type CertificateStatus,
  certificateStatuses,
  setIsCertificateInstancesRequested,
  setIsCompaniesRequested,
  setIsCertificateTypesRequested,
  CertificateScopeMaxLength,
  setCertificates,
} from '../../../Store/DataSlice'
import uuid4 from 'uuid4'
import './CertificateAdd.scss'
import { useEffect, useMemo, useState } from 'react'
import { TextInput } from '../../../Components/TextInput/TextInput'
import { useLocalization } from '../../../Hooks/useLocalization'
import { Button } from '../../../Components/Button/Button'
import { useDispatch, useSelector } from 'react-redux'
import { setDestinationView, showPopup } from '../../../Store/InterfaceSlice'
import { getAuthorizationHeader } from '../../../Orchestration/Authentication'
import { type RootState } from '../../../Store/Store'
import { DateInput } from '../../../Components/DateInput/DateInput'
import { CheckboxOption } from '../../../Components/CheckboxOption/CheckboxOption'
import { formatCompanyBranch } from '../../../Utils/Formatting'
import { getNaceCodeText, getNaceSelectOptions } from '../../../Utils/NaceCodes'
import { MultiSelect } from '../../../Components/MultiSelect/MultiSelect'
import { SingleSelect } from '../../../Components/SingleSelect/SingleSelect'
import { isEmpty } from '../../../Utils/Validations'
import { oneMonthInMs, oneYearInMs } from '../../../Utils/Constants'
import { LoadingSpinner } from '../../../Components/LoadingSpinner/LoadingSpinner'
import { sortResultString } from '../../../Utils/Arrays'

const emptyCertificate: Certificate = {
  uuid: uuid4(),
  certificateNumber: '',
  scope: '',
  naceCodes: [],
  companyBranches: [],
  status: 'Valid',
  certificateInstanceVatNumber: '',
  type: '',
  validFrom: Date.now(),
  validUntil: Date.now(),
  extendedUntil: undefined,
  issueDate: Date.now(),
  isGroupCertificate: false,
}

export function CertificateAdd() {
  const dispatch = useDispatch()
  const localization = useLocalization('certificate')
  const certificateStatusesLocalization = useLocalization(
    'certificate-statuses'
  )
  const countryLocalization = useLocalization('country')
  const user = useSelector((state: RootState) => state.authentication.user)
  const [allNaceSelectOptions] = useState(getNaceSelectOptions())
  const [now] = useState(Date.now())
  const earliestIssueDate = new Date('2023-8-01').getTime() //  now - oneMonthInMs
  const isAdmin = user?.isAdmin ?? false

  const isCompaniesRequested = useSelector(
    (state: RootState) => state.data.isCompaniesRequested
  )
  useEffect(() => {
    if (!isCompaniesRequested) dispatch(setIsCompaniesRequested(true))
  }, [isCompaniesRequested, dispatch])
  const allCompanies = useSelector((state: RootState) => state.data.companies)

  const isCertificateInstancesRequested = useSelector(
    (state: RootState) => state.data.isCertificateInstancesRequested
  )
  useEffect(() => {
    if (!isCertificateInstancesRequested)
      dispatch(setIsCertificateInstancesRequested(true))
  }, [isCertificateInstancesRequested, dispatch])

  const allCertificateInstances = useSelector(
    (state: RootState) => state.data.certificateInstances
  )

  const isCertificateTypesRequested = useSelector(
    (state: RootState) => state.data.isCertificateTypesRequested
  )
  useEffect(() => {
    if (!isCertificateTypesRequested)
      dispatch(setIsCertificateTypesRequested(true))
  }, [isCertificateTypesRequested, dispatch])

  const allCertificateTypes = useSelector(
    (state: RootState) => state.data.certificateTypes
  )
  const allCertificates = useSelector(
    (state: RootState) => state.data.certificates
  )

  const [certificate, setCertificate] = useState<Certificate>({
    ...emptyCertificate,
    validFrom: now,
    validUntil: now + 3 * oneYearInMs,
    extendedUntil: undefined,
    issueDate: now,
  })
  useEffect(() => {
    if (
      allCertificateInstances.length > 0 &&
      !certificate.certificateInstanceVatNumber
    )
      setCertificate((p) => ({
        ...p,
        certificateInstanceVatNumber: allCertificateInstances[0].vatNumber,
      }))
  }, [allCertificateInstances, certificate])

  const certificateInstanceCertificates = useMemo(
    () =>
      allCertificates?.filter(
        (c) =>
          certificate?.certificateInstanceVatNumber ===
          c.certificateInstanceVatNumber
      ),
    [allCertificates, certificate?.certificateInstanceVatNumber]
  )

  const [companyBranchVatToAdd, setCompanyBranchVatToAdd] = useState('')

  const [validationErrors, setValidationErrors] = useState<validationError[]>(
    []
  )
  useEffect(() => {
    const newValidationErrors: validationError[] = []

    if (!certificate.certificateNumber)
      newValidationErrors.push({
        property: 'certificateNumber',
        type: 'isRequired',
      })

    if (
      certificate?.certificateNumber &&
      certificateInstanceCertificates.some(
        (c) =>
          c.uuid !== certificate.uuid &&
          c.certificateNumber === certificate.certificateNumber.trim()
      )
    )
      newValidationErrors.push({
        property: 'certificateNumber',
        type: 'isDuplicate',
      })

    if (certificate.naceCodes.length === 0)
      newValidationErrors.push({ property: 'naceCodes', type: 'isRequired' })
    if (!certificate.scope)
      newValidationErrors.push({ property: 'scope', type: 'isRequired' })
    if (!certificate.status)
      newValidationErrors.push({ property: 'status', type: 'isRequired' })
    if (!certificate.type)
      newValidationErrors.push({ property: 'type', type: 'isRequired' })
    if (!isEmpty(certificate.type)) {
      const certificateType = allCertificateTypes.find(
        (ct) => ct.name === certificate.type
      )
      if (
        certificateType &&
        certificateType.naceLimitation?.length > 0 &&
        certificate.companyBranches?.length > 0
      ) {
        if (
          allCompanies
            .filter((c) =>
              certificate.companyBranches.some(
                (cb) => cb.vatNumber === c.vatNumber
              )
            )
            .some((c) => !certificateType.naceLimitation.includes(c.naceCode))
        )
          newValidationErrors.push({
            property: 'type',
            type: 'requireNaceCode',
            extraData: {
              naceLimitation: certificateType.naceLimitation.join(', '),
            },
          })
      }
    }
    if (certificate.companyBranches.length === 0)
      newValidationErrors.push({
        property: 'companyBranches',
        type: 'isRequired',
      })
    if (
      certificate.isGroupCertificate &&
      certificate.companyBranches.some((cb) => cb.branchNumbers.length === 0)
    )
      newValidationErrors.push({
        property: 'companyBranchVatNumbers',
        type: 'isRequired',
      })
    if (!certificate.certificateInstanceVatNumber)
      newValidationErrors.push({
        property: 'certificateInstanceVatNumber',
        type: 'isRequired',
      })
    if (!certificate.validFrom)
      newValidationErrors.push({
        property: 'validFrom',
        type: 'isRequired',
      })
    if (!certificate.validUntil)
      newValidationErrors.push({
        property: 'validUntil',
        type: 'isRequired',
      })
    if (!certificate.issueDate)
      newValidationErrors.push({
        property: 'issueDate',
        type: 'isRequired',
      })

    if (certificate.status === 'Prolonged' && certificate.extendedUntil == null)
      newValidationErrors.push({
        property: 'extendedUntil',
        type: 'isRequired',
      })
    console.log('ValidationErrors: ', JSON.stringify(newValidationErrors))
    setValidationErrors(newValidationErrors)
  }, [
    allCertificateInstances,
    allCertificateTypes,
    allCompanies,
    certificate,
    certificateInstanceCertificates,
  ])

  const [saveInProgress, setSaveInProgress] = useState(false)
  const saveCertificate = () => {
    setSaveInProgress(true)
    const cleanedCertificate = { ...certificate }

    if (!cleanedCertificate.isGroupCertificate) {
      cleanedCertificate.companyBranches =
        cleanedCertificate.companyBranches.map((cb) => ({
          vatNumber: cb.vatNumber,
          branchNumbers: [],
        }))
    }

    cleanedCertificate.certificateNumber =
      cleanedCertificate.certificateNumber.trim()

    fetch(
      `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/certificate-add`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeader(),
        },
        body: JSON.stringify({
          certificate: cleanedCertificate,
        }),
      }
    )
      .then(() => {
        dispatch(setCertificates([...allCertificates, cleanedCertificate]))
        setTimeout(() => {
          dispatch(setDestinationView({ destinationView: 'certificate-list' }))
        }, 1)
      })
      .catch((err) => {
        console.warn(`fetch certificate-add failed: `, err)
      })
  }

  useEffect(() => {
    // special find to ensure it's a valid certificate instance vat number
    const userCertificateInstanceVatNumber = allCertificateInstances.find(
      (c) => c.vatNumber === user?.certificateInstanceVatNumber
    )?.vatNumber
    if (userCertificateInstanceVatNumber != null) {
      setCertificate((previous) => ({
        ...previous,
        certificateInstanceVatNumber: userCertificateInstanceVatNumber,
      }))
    }
  }, [allCertificateInstances, user?.certificateInstanceVatNumber])

  return (
    <div className="certificate-add">
      {saveInProgress ? (
        <div className="save-in-progress">
          <LoadingSpinner color="dark" />
          {localization.text(
            'certificate-save-in-progress',
            'Het certificaat word momenteel opgeslagen.'
          )}
        </div>
      ) : (
        <>
          <SingleSelect
            label={localization.text('type', 'Type')}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              setCertificate((previous) => ({ ...previous, type: newValue }))
            }}
            value={certificate.type}
            isValid={!validationErrors.some((ve) => ve.property === 'type')}
            helperText={
              validationErrors.find((ve) => ve.property === 'type')?.type ===
              'isRequired'
                ? localization.text('type-missing', 'Selecteer een geldig type')
                : validationErrors.find((ve) => ve.property === 'type')
                    ?.type === 'requireNaceCode'
                ? localization.text(
                    'type-required-nace-code',
                    'Dit type is enkel toepasbaar op ondernemingen met NACE code {naceLimitation}',
                    {
                      naceLimitation:
                        validationErrors.find(
                          (ve) =>
                            ve.property === 'type' &&
                            ve.type === 'requireNaceCode'
                        )?.extraData?.naceLimitation ?? '',
                    }
                  )
                : undefined
            }
            items={(allCertificateTypes ?? [])
              .filter((ct) => ct.isActive)
              .map((ct) => ({
                value: ct.name,
                label: ct.name,
              }))
              .sort((a, b) => sortResultString(a.label, b.label))}
          />

          <TextInput
            label={localization.text('certificateNumber', 'Certificaat nummer')}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              setCertificate((previous) => ({
                ...previous,
                certificateNumber: newValue,
              }))
            }}
            value={certificate.certificateNumber}
            isValid={
              !validationErrors.some(
                (ve) => ve.property === 'certificateNumber'
              )
            }
            helperText={
              validationErrors.find((ve) => ve.property === 'certificateNumber')
                ?.type === 'isRequired'
                ? localization.text(
                    'certificateNumber-missing',
                    'Geef een geldige certificaat nummer in'
                  )
                : validationErrors.find(
                    (ve) => ve.property === 'certificateNumber'
                  )?.type === 'isDuplicate'
                ? localization.text(
                    'certificateNumber-is-duplicate',
                    'Dit certificaat nummer is al in onze database'
                  )
                : undefined
            }
          />

          <MultiSelect
            label={localization.text('naceCodes', 'NACE codes')}
            isReadOnly={saveInProgress}
            items={allNaceSelectOptions}
            onChange={(items) => {
              setCertificate((p) => ({
                ...p,
                naceCodes: items,
                scope: items
                  .map((nc) => getNaceCodeText(nc))
                  .join(', ')
                  .substring(0, CertificateScopeMaxLength),
              }))
            }}
            values={certificate.naceCodes}
            isValid={
              !validationErrors.some((ve) => ve.property === 'naceCodes')
            }
            helperText={
              validationErrors.find((ve) => ve.property === 'naceCodes')
                ?.type === 'isRequired'
                ? localization.text(
                    'naceCodes-missing',
                    'Geef minstens één geldige NACE code in'
                  )
                : undefined
            }
          />
          <TextInput
            label={localization.text('scope', 'Scope')}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              setCertificate((previous) => ({
                ...previous,
                scope: newValue.substring(0, CertificateScopeMaxLength),
              }))
            }}
            value={certificate.scope}
            isValid={!validationErrors.some((ve) => ve.property === 'scope')}
            helperText={
              validationErrors.find((ve) => ve.property === 'scope')?.type ===
              'isRequired'
                ? localization.text(
                    'scope-missing',
                    'Geef een geldige scope in'
                  )
                : CertificateScopeMaxLength - certificate.scope.length < 250
                ? localization.text(
                    'scope-max-length-helper',
                    '{remainingLength}/{maxLength} tekens resterend',
                    {
                      remainingLength: `${
                        CertificateScopeMaxLength - certificate.scope.length
                      }`,
                      maxLength: `${CertificateScopeMaxLength}`,
                    }
                  )
                : undefined
            }
            multiLine={true}
          />

          <CheckboxOption
            label={localization.text(
              'isGroupCertificate',
              'Is multi-vestiging'
            )}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              setCertificate((previous) => ({
                ...previous,
                isGroupCertificate: newValue,
                ...(!newValue
                  ? {
                      companyBranches: previous.companyBranches?.map((cb) => ({
                        vatNumber: cb.vatNumber,
                        branchNumbers: [],
                      })),
                    }
                  : {}),
              }))
            }}
            value={certificate.isGroupCertificate}
          />

          <div className="company-branches">
            <div className="label">
              {localization.text(
                'companyBranches',
                'Ondernemingen en vestigingen'
              )}
            </div>
            {certificate.companyBranches.map((cb) => {
              const company = allCompanies.find(
                (c) => c.vatNumber === cb.vatNumber
              )
              if (!company) return null
              return (
                <div className="company-option" key={`${company.vatNumber}`}>
                  <div className="company-option-name">
                    <div className="company-remove">
                      <Button
                        size="small"
                        importance="secondary"
                        text="❌"
                        onClick={() => {
                          const companyBranches = [
                            ...certificate.companyBranches,
                          ].filter((cb) => cb.vatNumber !== company.vatNumber)
                          setCertificate((p) => ({
                            ...p,
                            companyBranches,
                          }))
                        }}
                      />
                    </div>
                    <div className="company-name">{company.name}</div>
                  </div>
                  {certificate.isGroupCertificate && (
                    <>
                      <div className="company-branches">
                        {company.branches.map((b) => {
                          const isSelected = cb.branchNumbers?.includes(
                            b.number
                          )
                          return (
                            <div
                              className="company-branch"
                              key={`${company.vatNumber}-${b.number}`}
                            >
                              <CheckboxOption
                                value={isSelected}
                                valueName={formatCompanyBranch(
                                  b,
                                  countryLocalization
                                )}
                                onChange={(newValue) => {
                                  const companyBranches = [
                                    ...certificate.companyBranches,
                                  ]
                                  const companyBranch = companyBranches.find(
                                    (companyBranch) =>
                                      companyBranch.vatNumber === cb.vatNumber
                                  )
                                  if (!companyBranch) return
                                  if (newValue) {
                                    companyBranch.branchNumbers.push(b.number)
                                  } else {
                                    companyBranch.branchNumbers =
                                      companyBranch.branchNumbers.filter(
                                        (bn) => bn !== b.number
                                      )
                                  }
                                  setCertificate((p) => ({
                                    ...p,
                                    companyBranches,
                                  }))
                                }}
                              />
                            </div>
                          )
                        })}
                        {cb.branchNumbers.length === 0 && (
                          <div className="select-branch-warning">
                            {localization.text(
                              'companyBranches-not-selected',
                              'Kies minstens één vestiging'
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )
            })}
            <div className="add-company-branch">
              <SingleSelect
                label={localization.text(
                  'addCompanyBranch',
                  'Selecteer onderneming of typ BTW nummer'
                )}
                items={allCompanies
                  .filter(
                    (company) =>
                      !certificate.companyBranches.some(
                        (cb) => cb.vatNumber === company.vatNumber
                      )
                  )
                  .map((company) => ({
                    value: company.vatNumber,
                    label: `${company.vatNumber}: ${company.name}`,
                  }))
                  .sort((a, b) => sortResultString(a.label, b.label))}
                isReadOnly={saveInProgress}
                onChange={(newValue) => {
                  setCompanyBranchVatToAdd(newValue)
                }}
                value={companyBranchVatToAdd}
                isRequired={true}
                isValid={
                  !validationErrors.some(
                    (ve) => ve.property === 'companyBranches'
                  )
                }
                helperText={
                  validationErrors.find(
                    (ve) => ve.property === 'companyBranches'
                  )?.type === 'isRequired'
                    ? localization.text(
                        'companyBranches-missing',
                        'Kies minstens één onderneming en vestiging'
                      )
                    : undefined
                }
              />
              <div className="buttons-row">
                <Button
                  importance={'secondary'}
                  text={localization.text(
                    'add-companyBranch',
                    'Selecteer onderneming'
                  )}
                  disabled={!companyBranchVatToAdd}
                  onClick={() => {
                    if (
                      certificate.companyBranches.some(
                        (cb) => cb.vatNumber === companyBranchVatToAdd
                      )
                    )
                      return
                    const company = allCompanies.find(
                      (c) => c.vatNumber === companyBranchVatToAdd
                    )
                    if (!company) return
                    setCertificate((p) => ({
                      ...p,
                      companyBranches: [
                        ...p.companyBranches,
                        {
                          vatNumber: company.vatNumber,
                          branchNumbers:
                            company.branches.length === 1
                              ? [company.branches[0].number]
                              : [],
                        },
                      ],
                    }))
                  }}
                />
                <Button
                  size="regular"
                  importance="secondary"
                  text={localization.text(
                    'create-new-company',
                    'Maak nieuwe onderneming aan'
                  )}
                  onClick={() => dispatch(showPopup({ type: 'company-add' }))}
                />
              </div>
            </div>
          </div>

          <SingleSelect
            label={localization.text('status', 'Status')}
            items={certificateStatuses.map((status) => ({
              value: status,
              label: status
                ? certificateStatusesLocalization.text(status, status)
                : '',
            }))}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              setCertificate((previous) => ({
                ...previous,
                status: newValue as CertificateStatus,
              }))
            }}
            value={certificate.status}
            isRequired={true}
            isValid={!validationErrors.some((ve) => ve.property === 'status')}
            helperText={
              validationErrors.find((ve) => ve.property === 'status')?.type ===
              'isRequired'
                ? localization.text('status-missing', 'Kies een status')
                : undefined
            }
          />

          {isAdmin ||
          !allCertificateInstances.some(
            (ci) => ci.vatNumber === user?.certificateInstanceVatNumber
          ) ? (
            <SingleSelect
              label={localization.text(
                'certificateInstances',
                'Certificatie instantie'
              )}
              items={allCertificateInstances
                .map((certificateInstances) => ({
                  value: certificateInstances.vatNumber,
                  label: certificateInstances.name,
                }))
                .sort((a, b) => sortResultString(a.label, b.label))}
              isReadOnly={saveInProgress}
              onChange={(newValue) => {
                console.log('set certificateInstanceVatNumber', newValue)
                setCertificate((previous) => ({
                  ...previous,
                  certificateInstanceVatNumber: newValue,
                }))
              }}
              value={certificate.certificateInstanceVatNumber}
              isRequired={true}
              isValid={
                !validationErrors.some(
                  (ve) => ve.property === 'certificateInstanceVatNumber'
                )
              }
              helperText={
                validationErrors.find(
                  (ve) => ve.property === 'certificateInstanceVatNumber'
                )?.type === 'isRequired'
                  ? localization.text(
                      'certificateInstanceVatNumber-missing',
                      'Kies een certificaat instantie'
                    )
                  : undefined
              }
            />
          ) : (
            <div className="display-field">
              <div className="display-label">
                {localization.text(
                  'certificateInstances',
                  'Certificatie instantie'
                )}
              </div>
              <div className="display-value">
                {allCertificateInstances.find(
                  (c) =>
                    c.vatNumber === certificate.certificateInstanceVatNumber
                )?.name ?? ''}
              </div>
            </div>
          )}

          <DateInput
            type="date"
            label={localization.text('issueDate', 'Uitgereikt op')}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              if (newValue !== undefined)
                setCertificate((previous) => ({
                  ...previous,
                  issueDate: newValue,
                }))
            }}
            value={certificate.issueDate}
            isValid={
              !validationErrors.some((ve) => ve.property === 'issueDate')
            }
            helperText={
              validationErrors.find((ve) => ve.property === 'issueDate')
                ?.type === 'isRequired'
                ? localization.text(
                    'extendedUntil-missing',
                    'Geef een geldige datum in'
                  )
                : undefined
            }
            min={isAdmin ? undefined : earliestIssueDate}
            max={isAdmin ? undefined : now}
          />

          <DateInput
            type="date"
            label={localization.text('validFrom', 'Geldig van')}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              if (newValue !== undefined)
                setCertificate((previous) => ({
                  ...previous,
                  validFrom: newValue,
                  validUntil: newValue + 3 * oneYearInMs,
                }))
            }}
            value={certificate.validFrom}
            isValid={
              !validationErrors.some((ve) => ve.property === 'validFrom')
            }
            helperText={
              validationErrors.find((ve) => ve.property === 'validFrom')
                ?.type === 'isRequired'
                ? localization.text(
                    'extendedUntil-missing',
                    'Geef een geldige datum in'
                  )
                : undefined
            }
            min={isAdmin ? undefined : earliestIssueDate}
            max={isAdmin ? undefined : now + oneMonthInMs}
          />

          <DateInput
            type="date"
            label={localization.text('validUntil', 'Geldig tot')}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              if (newValue !== undefined)
                setCertificate((previous) => ({
                  ...previous,
                  validUntil: newValue,
                }))
            }}
            value={certificate.validUntil}
            isValid={
              !validationErrors.some((ve) => ve.property === 'validUntil')
            }
            helperText={
              validationErrors.find((ve) => ve.property === 'validUntil')
                ?.type === 'isRequired'
                ? localization.text(
                    'extendedUntil-missing',
                    'Geef een geldige datum in'
                  )
                : undefined
            }
            min={isAdmin ? undefined : certificate.validFrom}
            max={isAdmin ? undefined : certificate.validFrom + 3 * oneYearInMs}
          />

          <DateInput
            type="date"
            label={localization.text('extendedUntil', 'Verlengd tot')}
            isReadOnly={saveInProgress || certificate.status !== 'Prolonged'}
            onChange={(newValue) => {
              setCertificate((previous) => ({
                ...previous,
                extendedUntil: newValue,
              }))
            }}
            value={certificate.extendedUntil}
            isValid={
              !validationErrors.some((ve) => ve.property === 'extendedUntil')
            }
            helperText={
              validationErrors.find((ve) => ve.property === 'extendedUntil')
                ?.type === 'isRequired'
                ? localization.text(
                    'extendedUntil-missing',
                    'Geef een geldige datum in'
                  )
                : undefined
            }
            min={isAdmin ? undefined : certificate.validUntil}
            max={
              isAdmin ? undefined : certificate.validUntil + 6 * oneMonthInMs
            }
          />

          <div className="buttons">
            <Button
              importance={'primary'}
              text={localization.text(
                'safe-add-button',
                'Voeg certificaat toe'
              )}
              onClick={saveCertificate}
              disabled={validationErrors.length > 0 || saveInProgress}
            />
          </div>
        </>
      )}
    </div>
  )
}

interface validationError {
  property: string
  type: 'isRequired' | 'format' | 'requireNaceCode' | 'isDuplicate'
  extraData?: Record<string, string>
}
